import React, { useMemo, useCallback, useState, useEffect, useRef } from "react"
import { Formik, Form as FormikForm } from "formik"
import * as Yup from "yup"
import { toast } from "react-toastify"
import {
  Label,
  Button,
  ButtonGroup,
  FormFeedback,
  Row,
  Col,
} from "reactstrap"
import ThemeModal from "components/Common/ThemeModal"
import InputField from "components/Common/InputField"
import FormikCreatableSelect from "components/Common/FormikCreatableSelect"
import VehicleIdSelect from "components/Common/VehicleIdSelect"

import {
  useCreateServiceInvoice,
  useEditServiceInvoice,
} from "hooks/useServiceInvoice"
import { useCarCareServices } from "hooks/useCarCareService"
import { useAccessoriesListsWithStock } from "hooks/useAccessoriesLists"
import { useAccessoryUseRequests } from "hooks/useAccessoryUseRequests"
import { useCustomers } from "hooks/useCustomer"
import { useFilteredVehicleOptions } from "hooks/stockFilterHook"
import { getMoneyReceipts } from "api/moneyReceiptApi"

const CHARGE_TYPES = [
  { value: "chargeable", label: "Chargeable" },
  { value: "package", label: "Package" },
  { value: "free", label: "Free" },
]

const emptyServiceItem = {
  picker: "",
  charge_type: "chargeable",
  car_care_service_id: "",
  name: "",
  unit_price: "",
  remarks: "",
}

const emptyAccessoryItem = {
  picker: "",
  charge_type: "chargeable",
  accessory_id: "",
  name: "",
  quantity: 1,
  unit_price: "",
  stock: 0,
  is_new: false,
  remarks: "",
}

const isArrayObject = v =>
  v && typeof v === "object" && !Array.isArray(v) ? v : null

const hasAnyField = obj => {
  if (!obj || typeof obj !== "object") return false
  return Object.values(obj).some(v => v !== "" && v !== null && v !== undefined)
}

const emptyCustomerInfo = () => ({
  id: "",
  name: "",
  mobile: "",
  email: "",
  cuid: "",
  customer_type: "",
  designation: "",
  company_name: "",
  full_address: "",
  bank_name: "",
  branch_name: "",
})

const emptyVehicleInfo = () => ({
  id: "",
  stock_id: "",
  full_chassis: "",
  full_engine: "",
  brand: "",
  model: "",
  year: "",
  color: "",
  cc: "",
  fuel_type: "",
})

const ServiceInvoiceAddEdit = ({
  modal,
  toggleModal,
  isEdit,
  editedData,
  preSelectedCustomer,
  preSelectedVehicle,
  onSuccess,
}) => {
  const { raw: rawOptions, isLoading: optionLoading } =
    useFilteredVehicleOptions()
  const { data: carCareData } = useCarCareServices({ all: true })
  const { data: accessoriesData } = useAccessoriesListsWithStock({ all: true })
  const { data: customersData, isLoading: customerLoading } = useCustomers({
    all: true,
  })
  const { data: pendingRequestsData } = useAccessoryUseRequests({
    status: "pending",
    all: true,
  })

  const [createdCustomers, setCreatedCustomers] = useState([])
  const [showCustomerPicker, setShowCustomerPicker] = useState(false)
  const [vehicleCustomerOptions, setVehicleCustomerOptions] = useState([])
  const [showCustomerDetails, setShowCustomerDetails] = useState(false)
  const [showVehicleDetails, setShowVehicleDetails] = useState(false)
  const formikRef = useRef(null)

  useEffect(() => {
    if (!modal) {
      setCreatedCustomers([])
      setShowCustomerPicker(false)
      setVehicleCustomerOptions([])
      setShowCustomerDetails(false)
      setShowVehicleDetails(false)
    }
  }, [modal])

  const { mutateAsync: createMutation, isPending: createLoading } =
    useCreateServiceInvoice()
  const { mutateAsync: editMutation, isPending: editLoading } =
    useEditServiceInvoice()

  // -------------------- Options --------------------
  const employeeOptions = useMemo(
    () =>
      (rawOptions?.employees ?? []).map(e => ({
        value: e.id?.toString(),
        label: e.name,
      })),
    [rawOptions?.employees]
  )

  const customerOptions = useMemo(() => {
    const list = Array.isArray(customersData?.data) ? customersData.data : []
    const dbCustomers = list.map(c => ({
      value: c.id?.toString(),
      label: c.mobile ? `${c.name} — ${c.mobile}` : c.name,
    }))
    if (
      preSelectedCustomer?.id &&
      !dbCustomers.some(o => o.value === preSelectedCustomer.id?.toString())
    ) {
      dbCustomers.unshift({
        value: preSelectedCustomer.id?.toString(),
        label: preSelectedCustomer.name,
      })
    }
    const created = createdCustomers.map(c => ({ value: c.id, label: c.name }))
    return [...dbCustomers, ...created]
  }, [customersData, createdCustomers, preSelectedCustomer])

  // General services — service type, no inventory.
  const serviceOptions = useMemo(
    () =>
      (carCareData?.data ?? [])
        .filter(s => (s.type ?? "general_service") === "general_service")
        .map(s => ({
          value: s.id?.toString() || "",
          label: s.name,
          car_care_service_id: s.id?.toString() || "",
          name: s.name,
          price: Number(s.default_price ?? 0),
        })),
    [carCareData]
  )

  // Accessories — inventory type, carry available stock.
  const accessoryOptions = useMemo(
    () =>
      (accessoriesData?.data ?? []).map(a => ({
        value: a.id?.toString() || "",
        label: a.name,
        accessory_id: a.id?.toString() || "",
        name: a.name,
        price: Number(a.sale_price ?? 0),
        stock: Number(a.available_stock ?? 0),
      })),
    [accessoriesData]
  )

  // Pending accessory use requests — used to auto-fill accessories section.
  const pendingRequestOptions = useMemo(() => {
    const list = Array.isArray(pendingRequestsData?.data)
      ? pendingRequestsData.data
      : []
    return list.map(r => {
      const vehicleLabel =
        r.vehicle?.stock_id ||
        (typeof r.vehicle_info === "string" ? r.vehicle_info : null) ||
        "N/A"
      const itemCount = r.items?.length ?? 0
      return {
        value: r.id?.toString() || "",
        label: `${r.request_no || `REQ#${r.id}`} — ${vehicleLabel} (${itemCount} item${
          itemCount === 1 ? "" : "s"
        })`,
        request: r,
      }
    })
  }, [pendingRequestsData])

  // -------------------- Initial values --------------------
  const initialValues = useMemo(() => {
    const sourceItems = editedData?.items ?? []

    const serviceItems = sourceItems
      .filter(item => item.item_type === "general_service")
      .map(item => ({
        picker: item.car_care_service_id?.toString() || "",
        charge_type: item.charge_type || "chargeable",
        car_care_service_id: item.car_care_service_id?.toString() || "",
        name: item.name || "",
        unit_price: item.unit_price ?? "",
        remarks: item.remarks || "",
      }))

    const accessoryItems = sourceItems
      .filter(item => item.item_type === "accessory")
      .map(item => {
        const accessoryId = item.accessory_id?.toString() || ""
        const option = accessoryOptions.find(o => o.accessory_id === accessoryId)
        return {
          picker: accessoryId,
          charge_type: item.charge_type || "chargeable",
          accessory_id: accessoryId,
          name: item.name || "",
          quantity: item.quantity || 1,
          unit_price: item.unit_price ?? "",
          stock: option?.stock ?? 0,
          is_new: false,
          remarks: item.remarks || "",
        }
      })

    let vehicleValue = ""
    if (editedData?.vehicle?.id) {
      vehicleValue = {
        label:
          editedData.vehicle.stock_id +
          " - " +
          (editedData.vehicle.full_chassis || ""),
        value: editedData.vehicle.id,
      }
    } else if (preSelectedVehicle && !isEdit) {
      vehicleValue = preSelectedVehicle.value
        ? preSelectedVehicle
        : {
            label:
              preSelectedVehicle.stock_id +
              " - " +
              (preSelectedVehicle.full_chassis || ""),
            value: preSelectedVehicle.id,
          }
    }

    const existingCustomerInfo = isArrayObject(editedData?.customer_info)
      ? editedData.customer_info
      : {}
    const existingVehicleInfo = isArrayObject(editedData?.vehicle_info)
      ? editedData.vehicle_info
      : {}

    return {
      customer_id:
        editedData?.customer_id?.toString() ||
        (!isEdit && preSelectedCustomer?.id
          ? preSelectedCustomer.id.toString()
          : ""),
      customer_info: existingCustomerInfo,
      vehicle_id: vehicleValue,
      vehicle_info: existingVehicleInfo,
      invoice_date: editedData?.invoice_date
        ? String(editedData.invoice_date).slice(0, 16)
        : "",
      served_by_id: editedData?.served_by_id?.toString() || "",
      package_price: editedData?.package_price ?? "",
      discount: editedData?.discount ?? "",
      accessory_use_request_id: editedData?.accessory_use_request_id?.toString() || "",
      service_items: serviceItems,
      accessory_items: accessoryItems,
    }
  }, [editedData, preSelectedCustomer, preSelectedVehicle, isEdit, accessoryOptions])

  const validationSchema = useMemo(
    () =>
      Yup.object({
        customer_id: Yup.string().required("Please select a customer"),
        invoice_date: Yup.date().required("Please select an invoice date"),
        package_price: Yup.number().test(
          "package-price-required",
          "Enter a package price greater than 0",
          function (value) {
            const { service_items = [], accessory_items = [] } = this.parent
            const anyPackage = [...service_items, ...accessory_items].some(
              it => it.charge_type === "package"
            )
            if (!anyPackage) {
              return true
            }
            return Number(value) > 0
          }
        ),
        discount: Yup.number().min(0, "Discount must be 0 or more"),
        service_items: Yup.array().of(
          Yup.object({
            picker: Yup.string().required("Select a service"),
            unit_price: Yup.number().min(0, "Price must be 0 or more"),
          })
        ),
        accessory_items: Yup.array().of(
          Yup.object({
            picker: Yup.string().required("Select an accessory"),
            quantity: Yup.number()
              .required("Enter quantity")
              .moreThan(0, "Quantity must be greater than 0")
              .max(Yup.ref("stock"), "Quantity exceeds available stock"),
            unit_price: Yup.number().min(0, "Price must be 0 or more"),
          })
        ),
      }),
    []
  )

  const handleCreateCustomer = useCallback(inputValue => {
    const tempId = `new_${Date.now()}_${Math.random()
      .toString(36)
      .substr(2, 9)}`
    setCreatedCustomers(prev => [...prev, { id: tempId, name: inputValue }])
  }, [])

  // Pre-fill the customer_info JSON from the selected customer record.
  // Runs whenever customer_id changes (including when a new one is created).
  const prefillCustomerInfo = useCallback(
    (formik, customerId) => {
      if (!customerId || String(customerId).startsWith("new_")) {
        if (customerId && String(customerId).startsWith("new_")) {
          const created = createdCustomers.find(c => c.id === customerId)
          if (created) {
            formik.setFieldValue("customer_info", {
              ...emptyCustomerInfo(),
              name: created.name,
            })
          }
        } else {
          formik.setFieldValue("customer_info", emptyCustomerInfo())
        }
        return
      }

      const list = Array.isArray(customersData?.data) ? customersData.data : []
      const found = list.find(c => String(c.id) === String(customerId))
      if (found) {
        formik.setFieldValue("customer_info", {
          ...emptyCustomerInfo(),
          id: found.id,
          name: found.name || "",
          mobile: found.mobile || "",
          email: found.email || "",
          cuid: found.cuid || "",
          customer_type: found.customer_type || "",
          designation: found.designation || "",
          company_name: found.company_name || "",
          full_address: found.full_address || "",
          bank_name: found.bank_name || "",
          branch_name: found.branch_name || "",
        })
      }
    },
    [customersData, createdCustomers]
  )

  // Pre-fill the vehicle_info JSON from the VehicleIdSelect's raw record.
  const handleVehicleChange = useCallback((formik, raw) => {
    if (!raw) {
      formik.setFieldValue("vehicle_info", emptyVehicleInfo())
      return
    }
    formik.setFieldValue("vehicle_info", {
      ...emptyVehicleInfo(),
      id: raw.id,
      stock_id: raw.stock_id || "",
      full_chassis: raw.full_chassis || "",
      full_engine: raw.full_engine || "",
      brand: raw.brand?.name || raw.vs_brand?.name || "",
      model: raw.model?.name || raw.vs_model?.name || "",
      year: raw.year?.name || raw.vs_year?.name || "",
      color:
        raw.exteriorColor?.name || raw.vs_ex_color?.name || raw.color || "",
      cc: raw.engineCc?.name || raw.vs_engine_cc?.name || raw.cc || "",
      fuel_type: raw.fuelType?.name || raw.vs_fuel_type?.name || "",
    })
  }, [])

  const dbAccessoryIds = useMemo(
    () => new Set(accessoryOptions.map(o => String(o.accessory_id))),
    [accessoryOptions]
  )

  // Build accessory_items from a pending request, replacing whatever is in the form.
  const applyPendingRequest = useCallback(
    async (formik, request) => {
      if (!request) {
        formik.setFieldValue("accessory_items", [])
        formik.setFieldValue("vehicle_id", "")
        formik.setFieldValue("customer_id", "")
        formik.setFieldValue("customer_info", emptyCustomerInfo())
        formik.setFieldValue("vehicle_info", emptyVehicleInfo())
        formik.setFieldValue("invoice_date", "")
        formik.setFieldValue("served_by_id", "")
        return
      }

      if (request.vehicle?.id) {
        const v = request.vehicle
        formik.setFieldValue("vehicle_id", {
          value: v.id,
          label:
            (v.stock_id || "") +
            (v.full_chassis ? " - " + v.full_chassis : ""),
        })
        handleVehicleChange(formik, v)
      }

      if (request.request_date) {
        formik.setFieldValue(
          "invoice_date",
          String(request.request_date).slice(0, 16)
        )
      }

      if (request.requested_by_id) {
        formik.setFieldValue(
          "served_by_id",
          String(request.requested_by_id)
        )
      }

      // Resolve customer from money receipts matched by full_chassis.
      const chassis = request.vehicle?.full_chassis
      if (chassis) {
        try {
          const res = await getMoneyReceipts({
            full_chassis: chassis,
            per_page: 50,
          })
          const items = Array.isArray(res?.data?.data) ? res.data.data : []

          const byCustomer = new Map()
          for (const mr of items) {
            const cid = mr.customer_id
            if (!cid) continue
            const existing = byCustomer.get(cid)
            if (!existing) {
              byCustomer.set(cid, {
                id: cid,
                name: mr.customer?.name || `Customer #${cid}`,
                mobile: mr.customer?.mobile || "",
                latestMrNo: mr.mr_no || "",
                latestMrDate: mr.issued_date || "",
                mrCount: 1,
              })
            } else {
              existing.mrCount += 1
            }
          }
          const distinct = Array.from(byCustomer.values()).sort((a, b) =>
            String(b.latestMrDate).localeCompare(String(a.latestMrDate))
          )

          if (distinct.length === 0) {
            toast.info(
              "No money receipt found for this vehicle — pick a customer."
            )
          } else if (distinct.length === 1) {
            formik.setFieldValue("customer_id", String(distinct[0].id))
            prefillCustomerInfo(formik, distinct[0].id)
            toast.success(
              `Customer auto-filled: ${distinct[0].name}.`
            )
          } else {
            setVehicleCustomerOptions(distinct)
            setShowCustomerPicker(true)
          }
        } catch (e) {
          console.error("Failed to fetch money receipts", e)
          toast.error("Could not look up customers for this vehicle.")
        }
      }

      const items = Array.isArray(request.items) ? request.items : []
      if (items.length === 0) {
        formik.setFieldValue("accessory_items", [])
        toast.info("This request has no items to pull.")
        return
      }

      const mapped = items.map(item => {
        const aid = item.accessory_id?.toString() || ""
        const option = accessoryOptions.find(o => o.accessory_id === aid)
        const isExisting = !!option
        return {
          picker: aid,
          charge_type: "package",
          accessory_id: aid,
          name: item.name || option?.name || "",
          quantity: Number(item.quantity) || 1,
          unit_price: option ? Number(option.price) || 0 : 0,
          stock: option ? Number(option.stock) || 0 : 0,
          is_new: !isExisting,
          remarks: item.remarks || "",
        }
      })

      formik.setFieldValue("accessory_items", mapped)
    },
    [accessoryOptions, handleVehicleChange, prefillCustomerInfo]
  )

  const onSubmit = useCallback(
    async (values, { resetForm }) => {
      if (
        values.service_items.length === 0 &&
        values.accessory_items.length === 0
      ) {
        toast.error("Add at least one service or accessory.")
        return
      }

      try {
        const resolveCustomer = id => {
          if (id && String(id).startsWith("new_")) {
            return createdCustomers.find(c => c.id === id)?.name ?? id
          }
          return id
        }

        const items = [
          ...values.service_items.map(item => ({
            item_type: "general_service",
            charge_type: item.charge_type,
            car_care_service_id: item.car_care_service_id || null,
            accessory_id: null,
            name: item.name,
            quantity: 1,
            unit_price: Number(item.unit_price) || 0,
            remarks: item.remarks || null,
          })),
          ...values.accessory_items.map(item => ({
            item_type: "accessory",
            charge_type: item.charge_type,
            car_care_service_id: null,
            accessory_id: item.accessory_id || null,
            name: item.name,
            quantity: Number(item.quantity),
            unit_price: Number(item.unit_price) || 0,
            remarks: item.remarks || null,
          })),
        ]

        const anyPackage = items.some(it => it.charge_type === "package")

        const payload = {
          customer_id: resolveCustomer(values.customer_id),
          customer_info: hasAnyField(values.customer_info)
            ? { ...values.customer_info, id: resolveCustomer(values.customer_id) }
            : null,
          vehicle_id: values.vehicle_id?.value || values.vehicle_id || null,
          vehicle_info: hasAnyField(values.vehicle_info)
            ? {
                ...values.vehicle_info,
                id: values.vehicle_id?.value || values.vehicle_id || null,
              }
            : null,
          invoice_date: values.invoice_date,
          served_by_id: values.served_by_id || null,
          package_price: anyPackage ? Number(values.package_price) || 0 : 0,
          discount: Number(values.discount) || 0,
          accessory_use_request_id: values.accessory_use_request_id || null,
          items,
        }

        const action = isEdit
          ? editMutation({ id: editedData.id, ...payload })
          : createMutation(payload)
        const res = await action
        toast.success(
          res?.message ??
            `Service invoice ${isEdit ? "updated" : "created"} successfully.`
        )
        resetForm()
        toggleModal()
        onSuccess?.()
      } catch (error) {
        console.error(error)
        toast.error("Failed to save service invoice.")
      }
    },
    [isEdit, editedData, editMutation, createMutation, toggleModal, onSuccess, createdCustomers]
  )

  const isSubmitDisabled = editLoading || createLoading

  return (
    <ThemeModal
      isOpen={modal}
      toggle={toggleModal}
      title={isEdit ? "Edit Service Invoice" : "Add Service Invoice"}
      size="lg"
    >
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
        enableReinitialize={isEdit}
      >
        {formik => {
          formikRef.current = formik
          const addServiceItem = () =>
            formik.setFieldValue("service_items", [
              ...formik.values.service_items,
              { ...emptyServiceItem },
            ])
          const removeServiceItem = index =>
            formik.setFieldValue(
              "service_items",
              formik.values.service_items.filter((_, i) => i !== index)
            )

          const addAccessoryItem = () =>
            formik.setFieldValue("accessory_items", [
              ...formik.values.accessory_items,
              { ...emptyAccessoryItem },
            ])
          const removeAccessoryItem = index =>
            formik.setFieldValue(
              "accessory_items",
              formik.values.accessory_items.filter((_, i) => i !== index)
            )

          const renderChargeType = (field, value) => (
            <ButtonGroup size="sm">
              {CHARGE_TYPES.map(ct => (
                <Button
                  key={ct.value}
                  type="button"
                  color={value === ct.value ? "primary" : "light"}
                  outline={value !== ct.value}
                  onClick={() => formik.setFieldValue(field, ct.value)}
                >
                  {ct.label}
                </Button>
              ))}
            </ButtonGroup>
          )

          const allItems = [
            ...formik.values.service_items,
            ...formik.values.accessory_items,
          ]
          const anyPackage = allItems.some(it => it.charge_type === "package")

          const servicesSubtotal = formik.values.service_items.reduce(
            (sum, it) =>
              it.charge_type === "chargeable"
                ? sum + (Number(it.unit_price) || 0)
                : sum,
            0
          )
          const accessoriesSubtotal = formik.values.accessory_items.reduce(
            (sum, it) =>
              it.charge_type === "chargeable"
                ? sum +
                  (Number(it.quantity) || 0) * (Number(it.unit_price) || 0)
                : sum,
            0
          )
          const packagePrice = anyPackage
            ? Number(formik.values.package_price) || 0
            : 0
          const subtotal = servicesSubtotal + accessoriesSubtotal + packagePrice
          const discount = Number(formik.values.discount) || 0
          const grandTotal = Math.max(subtotal - discount, 0)

          const totalItems =
            formik.values.service_items.length +
            formik.values.accessory_items.length
          const showNoItemsError = formik.submitCount > 0 && totalItems === 0

          return (
            <FormikForm>
              {/* Customer */}
              <FormikCreatableSelect
                name="customer_id"
                label="Customer"
                formik={formik}
                options={customerOptions}
                placeholder="Select customer or type a name to add"
                loading={customerLoading}
                onCreateOption={handleCreateCustomer}
                onChange={value => prefillCustomerInfo(formik, value)}
                required
              />

              <Button
                color="link"
                size="sm"
                type="button"
                className="p-0 mb-2"
                onClick={() => setShowCustomerDetails(v => !v)}
              >
                {showCustomerDetails ? "Hide" : "Edit"} customer details
              </Button>

              {showCustomerDetails && (
                <div className="border rounded p-3 mb-3 bg-light">
                  <Row className="g-2">
                    <Col md={6}>
                      <Label>Name</Label>
                      <InputField
                        name="customer_info.name"
                        value={formik.values.customer_info?.name || ""}
                        onChange={formik.handleChange}
                      />
                    </Col>
                    <Col md={6}>
                      <Label>Mobile</Label>
                      <InputField
                        name="customer_info.mobile"
                        value={formik.values.customer_info?.mobile || ""}
                        onChange={formik.handleChange}
                      />
                    </Col>
                    <Col md={6}>
                      <Label>Email</Label>
                      <InputField
                        type="email"
                        name="customer_info.email"
                        value={formik.values.customer_info?.email || ""}
                        onChange={formik.handleChange}
                      />
                    </Col>
                    <Col md={6}>
                      <Label>CUID</Label>
                      <InputField
                        name="customer_info.cuid"
                        value={formik.values.customer_info?.cuid || ""}
                        onChange={formik.handleChange}
                      />
                    </Col>
                    <Col md={6}>
                      <Label>Customer Type</Label>
                      <InputField
                        name="customer_info.customer_type"
                        value={formik.values.customer_info?.customer_type || ""}
                        onChange={formik.handleChange}
                      />
                    </Col>
                    <Col md={6}>
                      <Label>Designation</Label>
                      <InputField
                        name="customer_info.designation"
                        value={formik.values.customer_info?.designation || ""}
                        onChange={formik.handleChange}
                      />
                    </Col>
                    <Col md={6}>
                      <Label>Company Name</Label>
                      <InputField
                        name="customer_info.company_name"
                        value={formik.values.customer_info?.company_name || ""}
                        onChange={formik.handleChange}
                      />
                    </Col>
                    <Col md={6}>
                      <Label>Bank Name</Label>
                      <InputField
                        name="customer_info.bank_name"
                        value={formik.values.customer_info?.bank_name || ""}
                        onChange={formik.handleChange}
                      />
                    </Col>
                    <Col md={6}>
                      <Label>Branch Name</Label>
                      <InputField
                        name="customer_info.branch_name"
                        value={formik.values.customer_info?.branch_name || ""}
                        onChange={formik.handleChange}
                      />
                    </Col>
                    <Col md={12}>
                      <Label>Address</Label>
                      <InputField
                        type="textarea"
                        name="customer_info.full_address"
                        value={formik.values.customer_info?.full_address || ""}
                        onChange={formik.handleChange}
                      />
                    </Col>
                  </Row>
                </div>
              )}

              {/* Vehicle */}
              <VehicleIdSelect
                formik={formik}
                onVehicleSelect={raw => handleVehicleChange(formik, raw)}
              />

              <Button
                color="link"
                size="sm"
                type="button"
                className="p-0 mb-2"
                onClick={() => setShowVehicleDetails(v => !v)}
              >
                {showVehicleDetails ? "Hide" : "Edit"} vehicle details
              </Button>

              {showVehicleDetails && (
                <div className="border rounded p-3 mb-3 bg-light">
                  <Row className="g-2">
                    <Col md={6}>
                      <Label>Stock ID</Label>
                      <InputField
                        name="vehicle_info.stock_id"
                        value={formik.values.vehicle_info?.stock_id || ""}
                        onChange={formik.handleChange}
                      />
                    </Col>
                    <Col md={6}>
                      <Label>Full Chassis</Label>
                      <InputField
                        name="vehicle_info.full_chassis"
                        value={formik.values.vehicle_info?.full_chassis || ""}
                        onChange={formik.handleChange}
                      />
                    </Col>
                    <Col md={6}>
                      <Label>Full Engine</Label>
                      <InputField
                        name="vehicle_info.full_engine"
                        value={formik.values.vehicle_info?.full_engine || ""}
                        onChange={formik.handleChange}
                      />
                    </Col>
                    <Col md={6}>
                      <Label>Brand</Label>
                      <InputField
                        name="vehicle_info.brand"
                        value={formik.values.vehicle_info?.brand || ""}
                        onChange={formik.handleChange}
                      />
                    </Col>
                    <Col md={6}>
                      <Label>Model</Label>
                      <InputField
                        name="vehicle_info.model"
                        value={formik.values.vehicle_info?.model || ""}
                        onChange={formik.handleChange}
                      />
                    </Col>
                    <Col md={6}>
                      <Label>Year</Label>
                      <InputField
                        name="vehicle_info.year"
                        value={formik.values.vehicle_info?.year || ""}
                        onChange={formik.handleChange}
                      />
                    </Col>
                    <Col md={6}>
                      <Label>Color</Label>
                      <InputField
                        name="vehicle_info.color"
                        value={formik.values.vehicle_info?.color || ""}
                        onChange={formik.handleChange}
                      />
                    </Col>
                    <Col md={6}>
                      <Label>Engine CC</Label>
                      <InputField
                        name="vehicle_info.cc"
                        value={formik.values.vehicle_info?.cc || ""}
                        onChange={formik.handleChange}
                      />
                    </Col>
                    <Col md={6}>
                      <Label>Fuel Type</Label>
                      <InputField
                        name="vehicle_info.fuel_type"
                        value={formik.values.vehicle_info?.fuel_type || ""}
                        onChange={formik.handleChange}
                      />
                    </Col>
                  </Row>
                </div>
              )}

              <Row>
                <Col md={6}>
                  <div className="mb-3">
                    <Label htmlFor="invoice_date">
                      Invoice Date <span className="text-danger">*</span>
                    </Label>
                    <InputField
                      type="datetime-local"
                      name="invoice_date"
                      id="invoice_date"
                      value={formik.values.invoice_date}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      invalid={
                        formik.touched.invoice_date &&
                        !!formik.errors.invoice_date
                      }
                    />
                    {formik.touched.invoice_date &&
                      formik.errors.invoice_date && (
                        <FormFeedback type="invalid" className="d-block">
                          {formik.errors.invoice_date}
                        </FormFeedback>
                      )}
                  </div>
                </Col>
                <Col md={6}>
                  <FormikCreatableSelect
                    name="served_by_id"
                    label="Served By"
                    formik={formik}
                    options={employeeOptions}
                    placeholder="Select employee"
                    loading={optionLoading}
                  />
                </Col>
              </Row>

              {showNoItemsError && (
                <div className="text-danger small mb-2">
                  Add at least one service or accessory.
                </div>
              )}

              {/* General Services — service type */}
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <Label className="mb-0 fw-semibold">
                    General Services{" "}
                    <span className="badge bg-light text-muted ms-1">
                      {formik.values.service_items.length}
                    </span>
                  </Label>
                  <Button
                    color="primary"
                    size="sm"
                    outline
                    type="button"
                    onClick={addServiceItem}
                  >
                    <i className="mdi mdi-plus me-1" />
                    Add Service
                  </Button>
                </div>

                {formik.values.service_items.length === 0 ? (
                  <div className="text-muted small border rounded p-3">
                    No services added.
                  </div>
                ) : (
                  <div className="border rounded">
                    {formik.values.service_items.map((item, index) => (
                      <div
                        key={index}
                        className={`p-3 ${index > 0 ? "border-top" : ""}`}
                      >
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <span className="badge bg-primary-subtle text-primary">
                            #{index + 1}
                          </span>
                          {renderChargeType(
                            `service_items.${index}.charge_type`,
                            item.charge_type
                          )}
                          <Button
                            color="link"
                            size="sm"
                            type="button"
                            className="text-danger p-0 lh-1"
                            onClick={() => removeServiceItem(index)}
                            title="Remove service"
                          >
                            <i className="mdi mdi-delete-outline fs-5" />
                          </Button>
                        </div>

                        <FormikCreatableSelect
                          name={`service_items.${index}.picker`}
                          label="Service"
                          formik={formik}
                          options={serviceOptions}
                          placeholder="Select a general service"
                          required
                          onChange={(value, selected) => {
                            if (!selected) return
                            // Existing pick carries an id; a typed-in name does not.
                            formik.setFieldValue(
                              `service_items.${index}.car_care_service_id`,
                              selected.car_care_service_id || selected.value
                            )
                            formik.setFieldValue(
                              `service_items.${index}.name`,
                              selected.name || selected.label
                            )
                            formik.setFieldValue(
                              `service_items.${index}.unit_price`,
                              selected.price || 0
                            )
                          }}
                        />

                        <Row className="g-2">
                          {item.charge_type === "chargeable" && (
                            <Col md={6}>
                              <Label>Price</Label>
                              <InputField
                                type="number"
                                name={`service_items.${index}.unit_price`}
                                value={item.unit_price}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                min="0"
                              />
                            </Col>
                          )}
                          <Col md={item.charge_type === "chargeable" ? 6 : 12}>
                            <Label>Remarks</Label>
                            <InputField
                              type="text"
                              name={`service_items.${index}.remarks`}
                              value={item.remarks}
                              onChange={formik.handleChange}
                            />
                          </Col>
                        </Row>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Pending Accessory Use Request — pulls items into Accessories */}
              {!isEdit && (
                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <Label className="mb-0 fw-semibold">
                      Pending Accessory Use Request
                    </Label>
                    {formik.values.accessory_use_request_id && (
                      <Button
                        color="link"
                        size="sm"
                        type="button"
                        className="p-0 text-danger"
                        onClick={() => {
                          formik.setFieldValue("accessory_use_request_id", "")
                          formik.setFieldValue("accessory_items", [])
                          formik.setFieldValue("vehicle_id", "")
                          formik.setFieldValue("customer_id", "")
                          formik.setFieldValue("customer_info", emptyCustomerInfo())
                          formik.setFieldValue("vehicle_info", emptyVehicleInfo())
                          formik.setFieldValue("invoice_date", "")
                          formik.setFieldValue("served_by_id", "")
                          setShowCustomerPicker(false)
                          setVehicleCustomerOptions([])
                        }}
                      >
                        Clear request
                      </Button>
                    )}
                  </div>
                  <FormikCreatableSelect
                    name="accessory_use_request_id"
                    label="Pending Request"
                    formik={formik}
                    options={pendingRequestOptions}
                    placeholder="Select a pending request (optional)"
                    onChange={(value, selected) => {
                      const request = selected?.request
                      if (!request) {
                        formik.setFieldValue("accessory_items", [])
                        return
                      }
                      if (
                        formik.values.accessory_items.length > 0
                      ) {
                        toast.info(
                          "Pulled the request's items (Package) and vehicle — enter a Package Price."
                        )
                      }
                      applyPendingRequest(formik, request)
                    }}
                  />
                  {formik.values.accessory_use_request_id && (() => {
                    const selected = pendingRequestOptions.find(
                      o =>
                        o.value ===
                        String(formik.values.accessory_use_request_id)
                    )
                    const req = selected?.request
                    if (!req) return null
                    return (
                      <div className="border rounded p-2 mt-2 bg-light">
                        <div className="d-flex justify-content-between mb-1">
                          <span className="fw-semibold small">
                            {req.request_no || `REQ#${req.id}`}
                          </span>
                          <span className="text-muted small">
                            {req.items?.length || 0} item
                            {req.items?.length === 1 ? "" : "s"}
                          </span>
                        </div>
                        <ul className="mb-0 small ps-3">
                          {(req.items || []).map((it, idx) => (
                            <li key={idx}>
                              {it.name || `Accessory #${it.accessory_id}`} — qty{" "}
                              {it.quantity}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )
                  })()}
                </div>
              )}

              {/* Accessories — inventory type */}
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <Label className="mb-0 fw-semibold">
                    Accessories{" "}
                    <span className="badge bg-light text-muted ms-1">
                      {formik.values.accessory_items.length}
                    </span>
                  </Label>
                  <Button
                    color="primary"
                    size="sm"
                    outline
                    type="button"
                    onClick={addAccessoryItem}
                  >
                    <i className="mdi mdi-plus me-1" />
                    Add Accessory
                  </Button>
                </div>

                {formik.values.accessory_items.length === 0 ? (
                  <div className="text-muted small border rounded p-3">
                    No accessories added.
                  </div>
                ) : (
                  <div className="border rounded">
                    {formik.values.accessory_items.map((item, index) => (
                      <div
                        key={index}
                        className={`p-3 ${index > 0 ? "border-top" : ""}`}
                      >
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <span className="badge bg-primary-subtle text-primary">
                            #{index + 1}
                          </span>
                          {renderChargeType(
                            `accessory_items.${index}.charge_type`,
                            item.charge_type
                          )}
                          <Button
                            color="link"
                            size="sm"
                            type="button"
                            className="text-danger p-0 lh-1"
                            onClick={() => removeAccessoryItem(index)}
                            title="Remove accessory"
                          >
                            <i className="mdi mdi-delete-outline fs-5" />
                          </Button>
                        </div>

                        <FormikCreatableSelect
                          name={`accessory_items.${index}.picker`}
                          label="Accessory"
                          formik={formik}
                          options={accessoryOptions}
                          placeholder="Select an accessory"
                          required
                          onChange={(value, selected) => {
                            if (!selected) return
                            // Existing pick carries an id; a typed-in name does not.
                            const isExisting = !!selected.accessory_id
                            formik.setFieldValue(
                              `accessory_items.${index}.accessory_id`,
                              isExisting ? selected.accessory_id : selected.value
                            )
                            formik.setFieldValue(
                              `accessory_items.${index}.name`,
                              selected.name || selected.label
                            )
                            formik.setFieldValue(
                              `accessory_items.${index}.unit_price`,
                              selected.price || 0
                            )
                            formik.setFieldValue(
                              `accessory_items.${index}.stock`,
                              isExisting
                                ? selected.stock || 0
                                : Number.MAX_SAFE_INTEGER
                            )
                            formik.setFieldValue(
                              `accessory_items.${index}.is_new`,
                              !isExisting
                            )
                          }}
                        />

                        {item.accessory_id && (
                          <div className="mb-2">
                            {item.is_new ? (
                              <span className="badge bg-info-subtle text-info">
                                New accessory — will be created
                              </span>
                            ) : (
                              <span
                                className={`badge ${
                                  item.stock > 0
                                    ? "bg-success-subtle text-success"
                                    : "bg-danger-subtle text-danger"
                                }`}
                              >
                                In stock: {item.stock}
                              </span>
                            )}
                          </div>
                        )}

                        <Row className="g-2">
                          <Col md={item.charge_type === "chargeable" ? 4 : 6}>
                            <Label>
                              Quantity <span className="text-danger">*</span>
                            </Label>
                            <InputField
                              type="number"
                              name={`accessory_items.${index}.quantity`}
                              value={item.quantity}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              min="1"
                              max={
                                item.is_new ? undefined : item.stock || undefined
                              }
                            />
                          </Col>
                          {item.charge_type === "chargeable" && (
                            <Col md={4}>
                              <Label>Unit Price</Label>
                              <InputField
                                type="number"
                                name={`accessory_items.${index}.unit_price`}
                                value={item.unit_price}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                min="0"
                              />
                            </Col>
                          )}
                          <Col md={item.charge_type === "chargeable" ? 4 : 6}>
                            <Label>Remarks</Label>
                            <InputField
                              type="text"
                              name={`accessory_items.${index}.remarks`}
                              value={item.remarks}
                              onChange={formik.handleChange}
                            />
                          </Col>
                        </Row>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Package price */}
              {anyPackage && (
                <div className="mb-3">
                  <Label>
                    Package Price <span className="text-danger">*</span>
                  </Label>
                  <InputField
                    type="number"
                    name="package_price"
                    value={formik.values.package_price}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    min="0"
                    invalid={
                      formik.touched.package_price &&
                      !!formik.errors.package_price
                    }
                  />
                  {formik.touched.package_price &&
                    formik.errors.package_price && (
                      <FormFeedback type="invalid" className="d-block">
                        {formik.errors.package_price}
                      </FormFeedback>
                    )}
                  <small className="text-muted">
                    One flat price covering every line marked “Package”.
                  </small>
                </div>
              )}

              {/* Discount */}
              <div className="mb-3">
                <Label>Discount</Label>
                <InputField
                  type="number"
                  name="discount"
                  value={formik.values.discount}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  min="0"
                />
              </div>

              {/* Totals */}
              <div className="card border mb-3">
                <div className="card-body py-2">
                  <div className="d-flex justify-content-between">
                    <span>Sub Total</span>
                    <span>{subtotal.toLocaleString()}</span>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span>Discount</span>
                    <span>- {discount.toLocaleString()}</span>
                  </div>
                  <div className="d-flex justify-content-between fw-bold border-top pt-1 mt-1">
                    <span>Total</span>
                    <span>{grandTotal.toLocaleString()}</span>
                  </div>
                </div>
              </div>

              <div className="text-end mt-3">
                <Button color="success" type="submit" disabled={isSubmitDisabled}>
                  {isEdit ? "Save Changes" : "Add Invoice"}
                </Button>
              </div>
            </FormikForm>
          )
        }}
      </Formik>

      <ThemeModal
        isOpen={showCustomerPicker}
        toggle={() => setShowCustomerPicker(false)}
        title="Pick a customer"
        size="md"
      >
        <p className="text-muted small mb-3">
          Multiple customers are linked to money receipts for this vehicle.
          Pick one.
        </p>
        <div className="border rounded">
          {vehicleCustomerOptions.map((c, idx) => (
            <div
              key={c.id}
              className={`p-3 d-flex justify-content-between align-items-center ${
                idx > 0 ? "border-top" : ""
              }`}
            >
              <div>
                <div className="fw-semibold">{c.name}</div>
                <div className="small text-muted">
                  {c.mobile || "no mobile"} · {c.mrCount} MR
                  {c.mrCount === 1 ? "" : "s"} · latest {c.latestMrNo || "—"}
                </div>
              </div>
              <Button
                color="primary"
                size="sm"
                onClick={() => {
                  if (formikRef.current) {
                    formikRef.current.setFieldValue("customer_id", String(c.id))
                    prefillCustomerInfo(formikRef.current, c.id)
                  }
                  setShowCustomerPicker(false)
                }}
              >
                Select
              </Button>
            </div>
          ))}
        </div>
        <div className="text-end mt-3">
          <Button
            color="light"
            onClick={() => setShowCustomerPicker(false)}
          >
            Cancel
          </Button>
        </div>
      </ThemeModal>
    </ThemeModal>
  )
}

export default ServiceInvoiceAddEdit
